import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Eye, EyeOff, Loader2, Mail, Phone, User, MapPin, Building, Lock, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  dcMemberSchema,
  nonDcMemberSchema,
  countries,
  type DcMemberFormValues,
  type NonDcMemberFormValues,
  type UserType,
} from '@/lib/types/registration';
import { useAuthStore } from '@/lib/stores/auth';
import { toast } from 'sonner';
import { GoogleIcon } from '@/components/ui/google-icon';

interface RegistrationFormProps {
  userType: UserType;
  onBack: () => void;
  onSuccess: () => void;
}

export function RegistrationForm({ userType, onBack, onSuccess }: RegistrationFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  
  const { signUpWithEmail, loginWithGoogle, isLoading, error, clearError } = useAuthStore();

  const isDcMember = userType === 'dc_member';
  const schema = isDcMember ? dcMemberSchema : nonDcMemberSchema;

  const form = useForm<DcMemberFormValues | NonDcMemberFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      userType,
      firstName: '',
      surname: '',
      phoneNumber: '',
      country: '',
      ...(isDcMember ? { dcMembershipId: '' } : {}),
      ...(!isDcMember ? { ministryName: '', ministryState: '', ministryCountry: '' } : {}),
      email: '',
      password: '',
      confirmPassword: '',
    },
  });

  const onSubmit = async (values: DcMemberFormValues | NonDcMemberFormValues) => {
    try {
      clearError();
      
      const signUpResult = await signUpWithEmail(values.email, values.password, `${values.firstName} ${values.surname}`);
      
      if (!signUpResult.success) {
        toast.error(signUpResult.error || 'Registration failed');
        return;
      }

      if (signUpResult.requiresVerification) {
        setIsSuccess(true);
        return;
      }

      const profileData = {
        userType: values.userType,
        firstName: values.firstName,
        surname: values.surname,
        phoneNumber: values.phoneNumber,
        country: values.country,
        ...(values.userType === 'dc_member' ? { dcMembershipId: (values as DcMemberFormValues).dcMembershipId } : {}),
        ...(values.userType === 'visitor' ? { 
          ministryName: (values as NonDcMemberFormValues).ministryName,
          ministryState: (values as NonDcMemberFormValues).ministryState,
          ministryCountry: (values as NonDcMemberFormValues).ministryCountry
        } : {})
      };

      await useAuthStore.getState().completeRegistrationProfile(profileData);
      
      setIsSuccess(true);
      toast.success('Account created successfully!');
      
      setTimeout(() => {
        onSuccess();
      }, 1500);
    } catch (err) {
      console.error('Registration error:', err);
      toast.error(err instanceof Error ? err.message : 'Registration failed');
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      clearError();
      await loginWithGoogle();
      // Google OAuth will redirect, profile data can be updated after auth
    } catch (err) {
      console.error('Google sign-in error:', err);
    }
  };

  if (isSuccess) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center space-y-4 py-8 text-center">
            <CheckCircle className="h-16 w-16 text-success" />
            <div className="space-y-2">
              <h3 className="text-xl font-semibold">Check Your Email</h3>
              <p className="text-muted-foreground max-w-sm">
                We&apos;ve sent a verification link to your email address. 
                Please click the link to activate your account.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="space-y-1">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="gap-1"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
        </div>
        <CardTitle className="text-xl font-semibold text-center">
          {isDcMember ? 'DC Member Registration' : 'Visitor Registration'}
        </CardTitle>
        <CardDescription className="text-center">
          {isDcMember
            ? 'Create your account as a Dominion Mandate member'
            : 'Create your account as a visitor from another ministry'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Google Sign-In Option */}
        <Button
          variant="outline"
          onClick={handleGoogleSignIn}
          disabled={isLoading}
          className="mb-6 w-full justify-center gap-3 border-zinc-300 bg-white px-4 py-3 text-center text-zinc-900 shadow-sm hover:bg-zinc-50 dark:bg-zinc-950 dark:text-zinc-50 dark:hover:bg-zinc-900"
        >
          <GoogleIcon className="size-5 shrink-0" />
          <span>Continue with Google</span>
        </Button>

        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">
              Or continue with email
            </span>
          </div>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Name Fields */}
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First Name</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          placeholder="John"
                          className="pl-9"
                          disabled={isLoading}
                          {...field}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="surname"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Surname</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          placeholder="Doe"
                          className="pl-9"
                          disabled={isLoading}
                          {...field}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Phone Number */}
            <FormField
              control={form.control}
              name="phoneNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone Number</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        placeholder="+234 800 000 0000"
                        className="pl-9"
                        disabled={isLoading}
                        {...field}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Country */}
            <FormField
              control={form.control}
              name="country"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Country of Residence</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <select
                        className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 pl-9 text-sm shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50"
                        disabled={isLoading}
                        value={field.value}
                        onChange={field.onChange}
                      >
                        <option value="">Select your country</option>
                        {countries.map((country) => (
                          <option key={country.value} value={country.value}>
                            {country.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* DC Member Specific Fields */}
            {isDcMember && (
              <FormField
                control={form.control}
                name="dcMembershipId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>DC Membership ID <span className="text-muted-foreground">(Optional)</span></FormLabel>
                    <FormControl>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          placeholder="e.g., DC-2024-12345"
                          className="pl-9"
                          disabled={isLoading}
                          {...field}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Non-DC Member Specific Fields */}
            {!isDcMember && (
              <>
                <FormField
                  control={form.control}
                  name="ministryName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Ministry/Church Name</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Building className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                          <Input
                            placeholder="e.g., Grace Baptist Church"
                            className="pl-9"
                            disabled={isLoading}
                            {...field}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="ministryState"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Ministry State</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <MapPin className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                          <Input
                            placeholder="e.g., Lagos State"
                            className="pl-9"
                            disabled={isLoading}
                            {...field}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="ministryCountry"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Ministry Country</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <MapPin className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                          <select
                            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 pl-9 text-sm shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50"
                            disabled={isLoading}
                            value={field.value}
                            onChange={field.onChange}
                          >
                            <option value="">Select ministry country</option>
                            {countries.map((country) => (
                              <option key={country.value} value={country.value}>
                                {country.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}

            {/* Email */}
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email Address</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        placeholder="you@example.com"
                        type="email"
                        className="pl-9"
                        disabled={isLoading}
                        {...field}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Password */}
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        placeholder="••••••••"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        className="pr-10"
                        disabled={isLoading}
                        {...field}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <Eye className="h-4 w-4 text-muted-foreground" />
                        )}
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Confirm Password */}
            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirm Password</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        placeholder="••••••••"
                        type={showConfirmPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        className="pr-10"
                        disabled={isLoading}
                        {...field}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <Eye className="h-4 w-4 text-muted-foreground" />
                        )}
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating account...
                </>
              ) : (
                'Create Account'
              )}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
